import React from "react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import Badge from "../../../../components/common/Badge";

const ApprovalTab = ({ order, formatDate }) => {
  if (!order.approval?.required) {
    return (
      <div className="text-center py-6 sm:py-8 md:py-12 px-4">
        <CheckCircleIcon className="w-10 h-10 sm:w-12 sm:h-12 text-green-500 mx-auto mb-2 sm:mb-3" />
        <p className="text-sm sm:text-base text-gray-500">
          This order does not require approval
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Alert Banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <ExclamationCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h4 className="font-semibold text-yellow-900 text-sm sm:text-base mb-0.5 sm:mb-1">
              Approval Required
            </h4>
            <p className="text-xs sm:text-sm text-yellow-800 leading-relaxed">
              {order.approval.reason || "This order requires manager approval"}
            </p>
          </div>
        </div>
      </div>

      {/* Approval Details */}
      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 px-1">
          Approval Details
        </h3>
        <div className="bg-gray-50 rounded-lg sm:rounded-xl overflow-hidden">
          {/* Status Row - Always prominent */}
          <div className="flex items-center justify-between p-3 sm:p-4 bg-white border-b border-gray-100">
            <span className="text-sm sm:text-base text-gray-600">Status</span>
            <Badge
              variant={
                order.approval.status === "APPROVED"
                  ? "green"
                  : order.approval.status === "REJECTED"
                  ? "red"
                  : "yellow"
              }
              size="sm"
              className="text-xs sm:text-sm"
            >
              {order.approval.status || "PENDING"}
            </Badge>
          </div>

          {/* Details List */}
          <div className="divide-y divide-gray-100">
            {order.approval.submittedBy && (
              <>
                <div className="flex items-center justify-between p-3 sm:p-4">
                  <span className="text-xs sm:text-sm text-gray-500">
                    Submitted By
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 text-right max-w-[60%] truncate">
                    {order.approval.submittedBy.adminName || "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 sm:p-4">
                  <span className="text-xs sm:text-sm text-gray-500">
                    Submitted At
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 text-right">
                    {formatDate(order.approval.submittedBy.submittedAt)}
                  </span>
                </div>
              </>
            )}

            {order.approval.reviewedBy && (
              <>
                <div className="flex items-center justify-between p-3 sm:p-4">
                  <span className="text-xs sm:text-sm text-gray-500">
                    Reviewed By
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 text-right max-w-[60%] truncate">
                    {order.approval.reviewedBy.adminName || "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 sm:p-4">
                  <span className="text-xs sm:text-sm text-gray-500">
                    Reviewed At
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 text-right">
                    {formatDate(order.approval.reviewedBy.reviewedAt)}
                  </span>
                </div>
              </>
            )}

            {/* Show placeholder if no submission/review info */}
            {!order.approval.submittedBy && !order.approval.reviewedBy && (
              <div className="p-4 sm:p-6 text-center">
                <p className="text-xs sm:text-sm text-gray-400">
                  No approval activity yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rejection Reason (if rejected) */}
      {order.approval.status === "REJECTED" &&
        order.approval.rejectionReason && (
          <div className="bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
            <h4 className="font-semibold text-red-900 text-sm sm:text-base mb-1 sm:mb-2">
              Rejection Reason
            </h4>
            <p className="text-xs sm:text-sm text-red-800 leading-relaxed">
              {order.approval.rejectionReason}
            </p>
          </div>
        )}

      {/* Approval Notes (if approved) */}
      {order.approval.status === "APPROVED" && order.approval.notes && (
        <div className="bg-green-50 border border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
          <h4 className="font-semibold text-green-900 text-sm sm:text-base mb-1 sm:mb-2">
            Approval Notes
          </h4>
          <p className="text-xs sm:text-sm text-green-800 leading-relaxed">
            {order.approval.notes}
          </p>
        </div>
      )}
    </div>
  );
};

export default ApprovalTab;
